/**
 * Which domain each game lives on, and the routes it owns.
 *
 * This is deliberately a standalone module with no imports: `lib/domainConfig`
 * is used by middleware.ts, which runs in the Edge Runtime, so it cannot pull
 * in `games/index.ts` (that drags every handler, and with them lodash, into the
 * edge bundle).
 *
 * Each game's config.ts spreads its entry here, so this stays the single source
 * of truth rather than becoming another shadow registry.
 */
export interface GameDomain {
  allowedOn: "gumgum" | "tcgfan";
  /** Routes this game owns. A domain turns away routes owned by games not on it. */
  routes: string[];
}

export const GAME_DOMAINS = {
  "one-piece": {
    allowedOn: "gumgum",
    // One Piece owns the un-prefixed root routes on gumgum.gg.
    routes: [
      "/one-piece",
      "/card",
      "/cards",
      "/decklist",
      "/decklists",
      "/leader",
      "/groups",
      "/market-watch",
      "/sealed-sim",
      "/tier-list",
      "/videos",
      "/hypergeometric-calculator",
    ],
  },
  "union-arena": { allowedOn: "tcgfan", routes: ["/union-arena"] },
  riftbound: { allowedOn: "tcgfan", routes: ["/riftbound"] },
  naruto: { allowedOn: "tcgfan", routes: ["/naruto"] },
  palworld: { allowedOn: "tcgfan", routes: ["/palworld"] },
} satisfies Record<string, GameDomain>;

/**
 * The game tcgfan.com shows when no other game is asked for: what "/" redirects
 * to, and the one a saved preference is allowed to override.
 */
export const TCGFAN_DEFAULT_GAME = "palworld";

/** Every game on `domain`. Derived from GAME_DOMAINS, so it never goes stale. */
export function gamesOn(domain: "gumgum" | "tcgfan"): string[] {
  return Object.entries(GAME_DOMAINS)
    .filter(([, g]) => g.allowedOn === domain)
    .map(([id]) => id);
}

/** Whether `game` is one of the games on `domain`. */
export function isGameOn(domain: "gumgum" | "tcgfan", game: string): boolean {
  return gamesOn(domain).includes(game);
}

/**
 * Routes owned by games that do NOT live on `domain` — what that domain should
 * turn away.
 */
export function routesNotOn(domain: "gumgum" | "tcgfan"): string[] {
  return Object.values(GAME_DOMAINS)
    .filter((g) => g.allowedOn !== domain)
    .flatMap((g) => g.routes as string[]);
}
